import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip } from 'recharts';
import { Loader2, RefreshCw } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import EmptyState from '../components/EmptyState';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { formatCurrency, shortId } from '../lib/utils';

function AgePill({ days }) {
  let tone;
  if (days <= 2) tone = { color: 'var(--success-fg)', bg: 'var(--success-bg)', border: 'var(--success-border)' };
  else if (days <= 7) tone = { color: 'var(--warning-fg)', bg: 'var(--warning-bg)', border: 'var(--warning-border)' };
  else tone = { color: 'var(--danger-fg)', bg: 'var(--danger-bg)', border: 'var(--danger-border)' };
  const label = days <= 2 ? '<48h' : days <= 7 ? '2-7d' : '>7d';
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full font-mono text-[11px] font-semibold"
      style={{
        padding: '3px 10px',
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        color: tone.color,
      }}
    >
      <span className="h-[5px] w-[5px] rounded-full" style={{ background: tone.color }} />
      {label} · {days}d
    </span>
  );
}

export default function ClearingHealthPage() {
  const { t, lang } = useLang();
  const locale = lang === 'es' ? 'es' : 'en-US';
  const qc = useQueryClient();

  const audit = useQuery({
    queryKey: ['clearing', 'audit'],
    queryFn: async () => (await api.get('/settlements/audit/clearing')).data,
  });
  const series = useQuery({
    queryKey: ['clearing', 'series'],
    queryFn: async () => (await api.get('/dashboard/clearing-series?days=30')).data,
  });

  const data = audit.data || { unreconciled: [], aged_alerts: [] };
  const chartData = (series.data?.series || []).map((s) => ({ date: s.date.slice(5), balance: s.balance }));

  return (
    <div data-testid="clearing-health-page" className="animate-fade-slide-up">
      <PageHeader
        title={t('clearing.title')}
        subtitle={t('clearing.subtitle')}
        actions={
          <Button
            variant="secondary"
            size="default"
            onClick={() => qc.invalidateQueries({ queryKey: ['clearing'] })}
            data-testid="clearing-refresh-button"
          >
            <RefreshCw className="h-[14px] w-[14px]" /> {t('common.refresh')}
          </Button>
        }
      />

      <div className="premium-card mb-6" style={{ padding: '24px' }}>
        <div className="mb-5">
          <h3 className="text-[15px] font-bold tracking-tight text-foreground">
            Balance (30 días)
          </h3>
        </div>
        <div style={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="ch-goldArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D4A843" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#D4A843" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tickLine={false} axisLine={false} />
              <YAxis tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{
                  background: 'hsl(var(--chart-tooltip-bg))',
                  border: '1px solid var(--chart-tooltip-border)',
                  borderRadius: 10,
                  fontSize: 12,
                  padding: '10px 14px',
                  color: 'hsl(var(--foreground))',
                  boxShadow: 'var(--shadow-md)',
                }}
              />
              <Area
                type="monotone"
                dataKey="balance"
                stroke="#D4A843"
                fill="url(#ch-goldArea)"
                strokeWidth={2}
                dot={false}
                activeDot={{
                  r: 5,
                  fill: '#D4A843',
                  stroke: 'rgba(212,168,67,0.30)',
                  strokeWidth: 8,
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 premium-card overflow-hidden">
          <div className="px-6 py-4 border-b border-default">
            <h3 className="text-[14px] font-bold tracking-tight text-foreground">
              {t('clearing.unreconciled')}
            </h3>
          </div>
          <div className="p-0">
            {audit.isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : data.unreconciled.length === 0 ? (
              <div className="p-6">
                <EmptyState message={t('common.empty')} />
              </div>
            ) : (
              <Table data-testid="clearing-unreconciled-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('clearing.column_settlement')}</TableHead>
                    <TableHead className="text-right">{t('clearing.column_balance')}</TableHead>
                    <TableHead>{t('clearing.column_age')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.unreconciled.map((u) => (
                    <TableRow key={u.settlement_id}>
                      <TableCell className="font-mono text-[13px] text-muted-foreground font-medium">
                        {shortId(u.settlement_id, 14, 6)}
                      </TableCell>
                      <TableCell
                        className="text-right num font-bold"
                        style={{ color: u.balance < 0 ? 'var(--danger-fg)' : 'hsl(var(--foreground))' }}
                      >
                        {formatCurrency(u.balance, locale)}
                      </TableCell>
                      <TableCell><AgePill days={u.age_days} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 premium-card overflow-hidden">
          <div className="px-6 py-4 border-b border-default">
            <h3 className="text-[14px] font-bold tracking-tight text-foreground">
              {t('clearing.aged_alerts')}
            </h3>
          </div>
          <div>
            {data.aged_alerts.length === 0 ? (
              <div className="p-6">
                <EmptyState message={t('common.empty')} />
              </div>
            ) : (
              <Table data-testid="clearing-aged-alerts-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('clearing.column_settlement')}</TableHead>
                    <TableHead className="text-right">{t('clearing.column_balance')}</TableHead>
                    <TableHead>{t('clearing.column_age')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.aged_alerts.map((a) => (
                    <TableRow key={a.settlement_id}>
                      <TableCell className="font-mono text-[13px] text-muted-foreground font-medium">
                        {shortId(a.settlement_id, 14, 6)}
                      </TableCell>
                      <TableCell className="text-right num font-bold" style={{ color: 'var(--danger-fg)' }}>
                        {formatCurrency(a.balance, locale)}
                      </TableCell>
                      <TableCell><AgePill days={a.age_days} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
